import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import type { ProSearchData } from '../services/search.service';
import { getApiFieldName } from '../utils/dynamicColumns';
import { normFieldName, type FieldAliasMap } from '../utils/fieldAliases';
import { PRO_COLUMN_IDENTITIES } from '../utils/proColumnIdentities';
import { TraceCarrierCell } from './trace/traceColumn';
import type { TraceCarrierType } from '../services/tracking.service';
import { masterproSearchPath } from '../utils/proSearch';

/**
 * The shared PRO column set behind TanTable's card ("Multi Row View") mode.
 *
 * In card mode this list IS the card's field list: column VISIBILITY decides
 * which fields a card shows and column ORDER decides where — a column id here is
 * the id ProResultCard looks up in its field catalog (falling back to the header
 * label + the raw row value for ids the catalog doesn't know).
 *
 * Search Results and the LC Dashboards results/drill view both build their PRO
 * card columns from here so the two render identical cards. Hosts append their
 * own dynamic extras (user-selected invoice / line-item / orders fields) after
 * this static set.
 */

/** Rendered inside the card's own header strip, so they must not also flow into
 *  the card's field grid or the chip grid (chip column N must line up with card
 *  field column N). Module-level for stable identity: TanTable memoizes the
 *  ordered visible-column list on this array's reference. */
export const PRO_CARD_HEADER_COLUMN_IDS = ['pronumber', 'moveType', 'masterPro'];

/** The columns that start VISIBLE. Everything else in the static set starts
 *  hidden and is reachable from TanTable's column-visibility menu. Hosts derive
 *  their `defaultColumnVisibility` from this so both pages default the same. */
export const PRO_CARD_CORE_VISIBLE_IDS = [
  'pronumber', 'moveType', 'customerName', 'container', 'size', 'equipType',
  'lfd', 'puAppt', 'delivAppt', 'deliveryDriver', 'terminal',
  // SSLine starts visible only because it CARRIES the run-trace-on-SSLine control
  // (see TRACEABLE_COLUMNS). Terminal was already core, so trace-on-Terminal was
  // reachable out of the box while its SSLine twin sat behind the column picker —
  // an inconsistent pair for what users experience as one feature. Still hideable
  // like any other column for anyone who doesn't want it.
  'ssline',
];

/** The PRO column vocabulary, under the name this module has always exported it:
 *  the registry fields each static column already renders, which is what keeps the
 *  picker from offering one of them twice. Defined in utils/proColumnIdentities so
 *  the layout matcher can read it without importing a React module — see the
 *  rationale there. */
export const PRO_CARD_COVERED_FIELDNAMES: Readonly<Record<string, readonly string[]>> = PRO_COLUMN_IDENTITIES;

/** Flat lookup of every registry fieldname the static set already covers. */
export const PRO_CARD_COVERED_FIELDNAME_SET: ReadonlySet<string> = new Set(
  Object.values(PRO_CARD_COVERED_FIELDNAMES).flat(),
);

/**
 * What a host declares about the registry fields its own columns already render.
 *
 * The RECORD form (column id -> the fieldname(s) that column shows) is the one to
 * write: it answers both questions at once — which defs to suppress from the picker
 * AND *which column* covers each, which is what lets that column inherit the
 * registry's title. The SET form is the older, title-blind shape; it still
 * de-duplicates correctly, it just can't name the column, so those columns keep
 * their hand-written header.
 */
export type CoveredFieldMap = Readonly<Record<string, readonly string[]>>;
export type CoveredFields = ReadonlySet<string> | CoveredFieldMap;

/** Flatten either form to the fieldnames to withhold from the picker. */
export const coveredFieldnameSet = (covered: CoveredFields): ReadonlySet<string> =>
  covered instanceof Set ? covered : new Set(Object.values(covered).flat());

/** The grid always shows these, so the picker doesn't list them — and they keep
 *  their own header (Templates calls its key column "Template #", not "Pro No."). */
export const DEFAULT_ALWAYS_SHOWN_COLUMN_IDS: ReadonlyArray<string> = ['pronumber'];


/** As much of a column def as the title rules read. `accessorKey` is `unknown`
 *  because TanStack types it `keyof T`, which is not assignable to `string`. */
export interface ColumnLike {
  id?: string;
  accessorKey?: unknown;
  header?: unknown;
  meta?: unknown;
}

/** A column's grid id: its explicit `id`, else its accessor key. */
const columnId = (col: ColumnLike): string | undefined =>
  col.id ?? (typeof col.accessorKey === 'string' ? col.accessorKey : undefined);

/** The registry's names for a field one of a host's own columns already renders. */
export interface RegistryTitle {
  fieldname: string;
  /** The long name — the picker's label, and the header when there is no short one. */
  displayname: string;
  /** The short name the grid header shows. */
  abbreviation: string | null;
}

/** The header text for a titled column: the same `abbreviation || displayname`
 *  generateColumnDef stamps on a picker-ADDED column, so one field reads the same
 *  whether the grid built the column or the user added it. */
export const registryHeader = (t: RegistryTitle): string =>
  (t.abbreviation && t.abbreviation.trim()) || t.displayname;

/** A column whose `meta` says this opts out of registry titling — it keeps its own
 *  header and its own picker label. Set it on a column whose id collides with a
 *  registry fieldname but whose DATA is something else: the LC Resolution board's
 *  `createdby` is the reschedule's author, not the order's; CST's `bill_to` is the
 *  bill-to NAME while the registry's `billto` is the number; Driver Dispatch
 *  History's `destName` is that LEG's destination, not the order's delivery
 *  location. De-duplication is unaffected — this only governs the label. */
const optsOutOfTitling = (col: { meta?: unknown }): boolean =>
  (col.meta as { registryTitle?: boolean } | undefined)?.registryTitle === false;

/**
 * Which of a host's own columns already render a registry field, and under what
 * names the registry knows it.
 *
 * Deliberately the SAME three rules `buildProPickerFields` de-duplicates by, in one
 * function so the two can't drift: a column that suppresses a def is exactly a
 * column that inherits that def's names. That is the whole point — the field keeps
 * the column (and so its id, its saved layouts and any rule built on it) and only
 * changes what it is CALLED, so "Terminate Leg - Dest Location" doesn't read as
 * "Return To" on one grid and "Terminate" on the next.
 *
 * A declared entry listing SEVERAL fieldnames names no single title (the PRO card's
 * `dispatcher` cell is backed by both the dispatcher's name and the username behind
 * it), so it falls through to the name rules and keeps the host header if those miss
 * too.
 */
export function resolveRegistryTitles(
  columns: ReadonlyArray<ColumnLike>,
  fieldDefinitions: ReadonlyArray<{
    fieldname: string; displayname: string; abbreviation: string | null; is_displayable?: boolean;
  }>,
  {
    coveredFields = PRO_CARD_COVERED_FIELDNAMES,
    alwaysShownColumnIds = DEFAULT_ALWAYS_SHOWN_COLUMN_IDS,
  }: { coveredFields?: CoveredFields; alwaysShownColumnIds?: ReadonlyArray<string> } = {},
): Map<string, RegistryTitle> {
  const displayable = fieldDefinitions.filter((d) => d.is_displayable !== false);
  const byName = new Map(displayable.map((d) => [d.fieldname, d]));
  // Rules 1 and 1b: the raw fieldname and its column-id mapping. First def wins, so
  // the registry's own order decides any tie.
  const byNorm = new Map<string, (typeof displayable)[number]>();
  for (const d of displayable) {
    for (const key of [normFieldName(d.fieldname), normFieldName(getApiFieldName(d.fieldname))]) {
      if (!byNorm.has(key)) byNorm.set(key, d);
    }
  }
  // `ReadonlySet` is an interface, not a class, so the instanceof narrows the VALUE
  // but not the union — hence the cast on the record arm.
  const declared: CoveredFieldMap | undefined =
    coveredFields instanceof Set ? undefined : (coveredFields as CoveredFieldMap);

  const titles = new Map<string, RegistryTitle>();
  for (const col of columns) {
    const id = columnId(col);
    if (!id || alwaysShownColumnIds.includes(id) || optsOutOfTitling(col)) continue;
    const names = declared?.[id];
    const d = (names && names.length === 1 ? byName.get(names[0]) : undefined)
      ?? byNorm.get(normFieldName(id));
    if (d) {
      titles.set(id, { fieldname: d.fieldname, displayname: d.displayname, abbreviation: d.abbreviation });
    }
  }
  return titles;
}

/** A column that has declared it holds no registry field. Frozen and shared so the
 *  alias map's "matched by id alone" entries are one object, not one per column. */
const NO_IDENTITY: readonly string[] = Object.freeze([]);

/**
 * What a host's column ids MEAN in registry terms — the map a saved layout is
 * translated through when it moves between two grids that name the same field
 * differently (see FieldAliasMap).
 *
 * Deliberately the same three rules, in the same order, that `resolveRegistryTitles`
 * resolves a title by, because they answer the same question: a column that
 * displaces a definition is a column that IS that definition, whether you are about
 * to borrow its name or match a saved layout onto it. Sharing the resolution is
 * what stops a column from reading as "Last Free Day" while a View saved over the
 * registry's `lastfreedaydate` refuses to load onto it.
 *
 * The one divergence is the titling opt-out. A DECLARED entry still produces an
 * alias even on an opted-out column: `meta: { registryTitle: false }` says "do not
 * take that definition's NAME", which the existing de-duplication already ignores
 * for the same reason — a host naming the field a column renders is more
 * authoritative than a flag about labelling. What the opt-out does suppress is the
 * resemblance rule, by declaring the column to have no identity at all: that is
 * precisely the case it exists for (CST's `bill_to` is the bill-to NAME, the
 * registry's `billto` is the number), and matching those two would move a layout
 * onto a column holding different data.
 */
export function resolveFieldAliases(
  columns: ReadonlyArray<ColumnLike>,
  fieldDefinitions: ReadonlyArray<{
    fieldname: string; displayname: string; abbreviation: string | null; is_displayable?: boolean;
  }>,
  options: { coveredFields?: CoveredFields; alwaysShownColumnIds?: ReadonlyArray<string> } = {},
): FieldAliasMap {
  const { coveredFields = PRO_CARD_COVERED_FIELDNAMES } = options;
  const declared: CoveredFieldMap | undefined =
    coveredFields instanceof Set ? undefined : (coveredFields as CoveredFieldMap);
  const titles = resolveRegistryTitles(columns, fieldDefinitions, options);
  const aliases: Record<string, readonly string[]> = {};
  for (const col of columns) {
    const id = columnId(col);
    if (!id) continue;
    const names = declared?.[id];
    if (names && names.length) { aliases[id] = names; continue; }
    if (optsOutOfTitling(col)) { aliases[id] = NO_IDENTITY; continue; }
    const title = titles.get(id);
    if (title) aliases[id] = [title.fieldname];
  }
  return aliases;
}

/**
 * Re-title a host's columns from the registry, leaving everything else about them
 * alone — same id, same accessor, same cell renderer, so saved layouts, column
 * filters and anything built on the column id keep working.
 *
 * A non-string header is left untouched: it's a renderer (a sort control, an icon),
 * and replacing it with text would delete behaviour rather than rename it.
 * Returns the ORIGINAL array when nothing changed, so a memo keyed on it holds.
 */
export function applyRegistryTitles<C extends ColumnLike>(
  columns: C[],
  titles: ReadonlyMap<string, RegistryTitle>,
): C[] {
  if (!titles.size) return columns;
  let changed = false;
  const out = columns.map((col) => {
    const id = columnId(col);
    const title = id ? titles.get(id) : undefined;
    if (!title) return col;
    if (col.header !== undefined && typeof col.header !== 'string') return col;
    const header = registryHeader(title);
    if (header === col.header) return col;
    changed = true;
    return { ...col, header } as C;
  });
  return changed ? out : columns;
}

/** One row in the column picker's universe. Structurally compatible with
 *  FieldDefinition, but declared locally so this module stays free of the
 *  registry context. */
export interface ProPickerField {
  fieldname: string;
  displayname: string;
  abbreviation: string | null;
  type: string;
  expands: boolean;
}

interface ProPickerInput {
  /** The host's built columns — the static set plus any already-added extras. */
  columns: ReadonlyArray<{ id?: string; accessorKey?: string; header?: unknown; meta?: unknown }>;
  /** Every registry field def (filtered to displayable here). `expands` is
   *  optional on FieldDefinition, so it's coerced rather than required. */
  fieldDefinitions: ReadonlyArray<
    Omit<ProPickerField, 'expands'> & { expands?: boolean; is_displayable?: boolean }
  >;
  /** Fieldnames the user has already added (their columns are in `columns`). */
  additionalFields: ReadonlyArray<string>;
  /** Registry fieldnames a host's own column already renders under a DIFFERENT id,
   *  which rule 1 below structurally cannot see. Defaults to the PRO card's map,
   *  since that is this module's own grid; a host with its own columns (Repair
   *  Orders, the Dispatch Planner) passes its own, or an empty one. Prefer the
   *  record form — see CoveredFields. */
  coveredFields?: CoveredFields;
  /** Column ids to leave out of the picker entirely because the grid always shows
   *  them — the key column. Defaults to the PRO card's locked Pro #. */
  alwaysShownColumnIds?: ReadonlyArray<string>;
}

/**
 * The column picker's universe: the built-in static columns (so they stay
 * show/hide-able) PLUS every displayable registry field that isn't already one
 * of them.
 *
 * A registry field is dropped when ANY of
 *   1. its fieldname normalizes onto a static column id — catches the
 *      coincidental matches (`movetype`, `container`, `size`, `comments`…);
 *   1b. its MAPPED name does. A registry field becomes a column keyed on
 *      getApiFieldName(fieldname), so `revenueregion` would generate a column with
 *      id `region` — colliding outright with a host column already called `region`
 *      (Repair Orders' repair region is exactly this). Two columns sharing an id is
 *      a broken grid, and the attached value would overwrite the host's own; or
 *   2. PRO_CARD_COVERED_FIELDNAMES says a static column already renders it —
 *      catches the renamed ones rules 1/1b structurally cannot see, because the
 *      column id is a hand-picked name and the registry key is the raw CMS column
 *      (`rail` under "Terminal", `lastfreedaydate` under "LFD", …).
 * Rules 1b and 2 are the fix for those showing up as a second, identical column.
 *
 * A dropped def hands its NAMES to the column that displaced it (see
 * resolveRegistryTitles), so the picker lists that column under the registry's full
 * name with the short one beneath — the same two-line label an added field gets.
 */
export function buildProPickerFields(
  {
    columns,
    fieldDefinitions,
    additionalFields,
    coveredFields = PRO_CARD_COVERED_FIELDNAMES,
    alwaysShownColumnIds = DEFAULT_ALWAYS_SHOWN_COLUMN_IDS,
  }: ProPickerInput,
): ProPickerField[] {
  const coveredFieldnames = coveredFieldnameSet(coveredFields);
  const titles = resolveRegistryTitles(columns, fieldDefinitions, { coveredFields, alwaysShownColumnIds });
  const addedNorm = new Set(additionalFields.map(normFieldName));
  const staticEntries: ProPickerField[] = columns
    .map((c) => ({
      id: (c.id ?? c.accessorKey) as string | undefined,
      header: typeof c.header === 'string' ? c.header : '',
    }))
    // Drop the locked Pro # and any user-added field-def columns (those come from
    // the registry list below, so they'd otherwise double-list).
    .filter((c): c is { id: string; header: string } =>
      !!c.id && !alwaysShownColumnIds.includes(c.id) && !addedNorm.has(normFieldName(c.id)))
    .map((c) => {
      const title = titles.get(c.id);
      return {
        fieldname: c.id,
        displayname: title?.displayname || c.header || c.id,
        abbreviation: title?.abbreviation ?? null,
        type: 'string',
        expands: false,
      };
    });
  const staticNorm = new Set(staticEntries.map((e) => normFieldName(e.fieldname)));
  const defEntries = fieldDefinitions
    .filter((d) => d.is_displayable
      && !staticNorm.has(normFieldName(d.fieldname))
      && !staticNorm.has(normFieldName(getApiFieldName(d.fieldname)))
      && !coveredFieldnames.has(d.fieldname))
    .map((d) => ({
      fieldname: d.fieldname,
      displayname: d.displayname,
      abbreviation: d.abbreviation,
      type: d.type,
      expands: !!d.expands,
    }));
  return [...staticEntries, ...defEntries];
}

/** The two carrier columns that carry a run-trace control, and which side of the
 *  legacy trace each one forces (`scac` = SSLine, `rail` = Terminal) — the same
 *  split the per-field ➡️ buttons on /pro use. Keyed by COLUMN id here; the
 *  field-definition columns use TRACE_CARRIER_FIELDS, keyed by fieldname, for the
 *  same mapping. (The card view opts these ids into the host cell separately, via
 *  `hostRender` on their ProResultCard catalog entries.) */
const TRACEABLE_COLUMNS: Record<string, TraceCarrierType> = {
  ssline: 'scac',
  terminal: 'rail',
};

export interface ProCardColumnOptions {
  /** Enables the per-row run-trace ➡️ on the SSLine and Terminal cells, and is
   *  called after a non-error trace so the host can re-read that row (the trace
   *  writes LFD / Available / Out Gate / Trace Date …, which the on-screen row
   *  would otherwise keep showing at pre-trace values). Omitted = no trace
   *  controls, which is what a host that can't refresh a row should pass. */
  onTraced?: (pronumber: number) => void;
}

/** The static ~40-column PRO set. `onProClick` opens the order detail for the
 *  clicked Pro # and `opts.onTraced` turns on the carrier columns' run-trace
 *  buttons; those are the only interactive cells. */
export function buildProCardColumns(
  onProClick: (proNumber: string) => void,
  opts: ProCardColumnOptions = {},
): ColumnDef<ProSearchData>[] {
  const { onTraced } = opts;
  const emptyCell = <span className="text-gray-300 dark:text-gray-600">—</span>;

  /** A carrier column: its value, plus the v1 ➡️ that traces THIS pro on THIS
   *  carrier and shows the result in the same TraceResultModal /pro opens. The
   *  cell itself is TraceCarrierCell, shared with the field-definition-built
   *  columns on the LC bucket tables, so both look and behave the same.
   *
   *  `accessorKey` is untouched, so sorting, column filters, grouping and Excel
   *  export still see the plain carrier string.
   *
   *  NOTE: in grid-EDIT mode the host replaces `cell` wholesale with its editor,
   *  so the control disappears while a column is being edited. Neither carrier is
   *  grid-editable today, so this is latent rather than live. */
  const traceable = (
    id: 'ssline' | 'terminal',
    header: string,
    size: number,
  ): ColumnDef<ProSearchData> => ({
    id,
    accessorKey: id,
    header,
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    size,
    cell: ({ row, getValue }) => {
      const raw = getValue();
      const carrier = raw === null || raw === undefined ? '' : String(raw).trim();
      if (!carrier || carrier === '0') return emptyCell;
      return (
        <TraceCarrierCell
          pronumber={row.original.pronumber}
          carrier={carrier}
          tracetype={TRACEABLE_COLUMNS[id]}
          onTraced={onTraced}
        />
      );
    },
  });

  const text = (id: keyof ProSearchData, header: string, size = 130, mono = false): ColumnDef<ProSearchData> => ({
    id: id as string,
    accessorKey: id as string,
    header,
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    size,
    cell: ({ getValue }) => {
      const v = getValue() as React.ReactNode;
      const isEmpty = v === null || v === undefined || v === '' || v === '0';
      return isEmpty
        ? <span className="text-gray-300 dark:text-gray-600">—</span>
        : <span>{v}</span>;
    },
  });

  return [
    {
      id: 'pronumber',
      accessorKey: 'pronumber',
      header: 'Pro #',
      enableSorting: true,
      enableColumnFilter: true,
      enableHiding: false,
      size: 110,
      cell: ({ row }) => {
        // Viewed/last-viewed highlight is applied by TanTable's
        // trackViewedColumnId="pronumber" wrapper (the host's onProClick still
        // marks it viewed for the card view, which the wrapper doesn't cover).
        return (
          <button
            onClick={() => onProClick(String(row.original.pronumber))}
            className="font-semibold text-purple-700 hover:text-purple-900 dark:text-purple-300 dark:hover:text-purple-200 hover:underline"
            title="Open order details"
          >
            {row.original.pronumber}
          </button>
        );
      },
    },
    // --- Core (visible) ---
    text('moveType', 'Move Type', 120),
    text('customerName', 'Customer', 180),
    text('container', 'Container', 130, true),
    text('size', 'Size', 80),
    text('equipType', 'Type', 90),
    text('lfd', 'LFD', 110),
    text('puAppt', 'PU Appt', 130),
    text('delivAppt', 'Deliv Appt', 130),
    text('deliveryDriver', 'Driver (Del)', 110, true),
    traceable('terminal', 'Terminal', 130),
    // --- Hidden by default (toggleable) ---
    {
      ...text('masterPro', 'Master Pro', 110, true),
      // Opens every order on the master in a new tab (V1 fv_masterpro parity).
      cell: ({ getValue }) => {
        const v = getValue();
        const isEmpty = v === null || v === undefined || v === '' || v === '0';
        if (isEmpty) return <span className="text-gray-300 dark:text-gray-600">—</span>;
        return (
          <a
            href={masterproSearchPath(String(v))}
            target="_blank"
            rel="noopener noreferrer"
            title="All PROs on this Master PRO (new tab)"
            className="text-blue-600 dark:text-blue-400 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {String(v)}
          </a>
        );
      },
    },
    text('commodity', 'Commodity', 140),
    {
      id: 'csr', header: 'CSR', size: 120, enableSorting: true, enableColumnFilter: true, enableHiding: true,
      accessorFn: (d) => d.csrName || d.csr || '',
    },
    {
      id: 'whseCsr', header: 'Whse CSR', size: 120, enableSorting: true, enableColumnFilter: true, enableHiding: true,
      accessorFn: (d) => d.whseCsrName || d.whseCsr || '',
    },
    {
      id: 'dispatcher', header: 'Dispatcher', size: 120, enableSorting: true, enableColumnFilter: true, enableHiding: true,
      accessorFn: (d) => d.dispatcherName || d.dispatcher || '',
    },
    text('billToName', 'Bill To', 160),
    text('billFrom', 'Bill From', 130),
    text('billToRef', 'Bill To Ref #', 130, true),
    text('invDate', 'Inv Date', 110),
    text('chassisId', 'Chassis #', 120, true),
    text('isDrop', 'Drop', 70),
    traceable('ssline', 'SSLine', 120),
    text('vessel', 'Vessel', 130),
    text('voyage', 'Voyage', 100, true),
    text('po', 'PO', 110, true),
    text('bol', 'BOL/BKG/Ref #', 150, true),
    text('available', 'Available', 110),
    text('perDiem', 'Per Diem', 110),
    text('outGate', 'Out Gate', 110),
    text('docCut', 'Doc Cut', 110),
    text('cutoff', 'Cutoff', 110),
    {
      id: 'delivTimes', header: 'Deliv Times', size: 130, enableSorting: true, enableColumnFilter: true, enableHiding: true,
      accessorFn: (d) => [d.delivApptTime1, d.delivApptTime2].filter(Boolean).join(' / '),
    },
    text('pickupFrom', 'Pickup From', 160),
    text('deliverTo', 'Deliver To', 160),
    text('returnTo', 'Return To', 160),
    {
      id: 'cityStateZip', header: 'City, State Zip', size: 160, enableSorting: true, enableColumnFilter: true, enableHiding: true,
      accessorFn: (d) => [[d.deliverCity, d.deliverState].filter(Boolean).join(' '), d.deliverZip].filter(Boolean).join(', '),
    },
    text('terminateDriver', 'Driver (Term)', 110, true),
    text('deliveryDate', 'Delivery Date', 120),
    text('terminateDate', 'Terminate Date', 120),
    text('traceTerminate', 'Trace Terminate', 130),
    // --- Service Failure (latest reschedule w/ servicefailure='Y'); one row per PRO ---
    text('serviceFailureDate', 'Service Failure Date', 150),
    text('serviceFailureReason', 'Service Failure Reason', 170),
    text('serviceFailureSubReason', 'Service Failure Sub-Reason', 180),
    text('serviceFailureDriver', 'Service Failure Driver', 150),
    {
      id: 'serviceFailureCount', accessorKey: 'serviceFailureCount', header: '# Service Failures',
      size: 130, enableSorting: true, enableColumnFilter: true, enableHiding: true,
      cell: ({ getValue }) => {
        const n = getValue() as number | null | undefined;
        return n && n > 0
          ? <span>{n}</span>
          : <span className="text-gray-300 dark:text-gray-600">—</span>;
      },
    },
    {
      id: 'comments', header: 'Comments', size: 240, enableSorting: false, enableColumnFilter: true, enableHiding: true,
      accessorFn: (d) => (d.comments ? d.comments.split('\n').filter(Boolean).join(' • ') : ''),
    },
  ];
}
